import "./user-skill-gap-delta.css";
import { LuBriefcase } from "react-icons/lu";

// Short Report: User vs User - Skill Gap Delta
// Unique classnames prefixed with tt-usg- to avoid conflicts
const UserSkillGapDeltaReport = ({ comparisonData, reportKey }) => {
  console.log(comparisonData, "UserSkillGapDeltaReport data");
  // REPLACE the entire `const sr = ...` block with this guarded selector + default
  const DEFAULT_SR = {
    userA: {
      name: "Alice",
      missing_skills: [
        { skill_gap: "Machine Learning", type: "Job Skill" },
        { skill_gap: "Cloud Computing", type: "Job Skill" },
      ],
    },
    userB: {
      name: "Brian",
      missing_skills: [
        { skill_gap: "SQL", type: "Job Skill" },
        { skill_gap: "Data Visualization", type: "Job Skill" },
        { skill_gap: "Adaptability", type: "Soft Skill" },
      ],
    },
    overview:
      "Alice has two missing skills, both Job Skills—Machine Learning and Cloud Computing—indicating narrower, advanced technical gaps. Brian has three missing skills across Job Skills (SQL, Data Visualization) and a Soft Skill (Adaptability), reflecting broader upskilling needs. Overall, Alice is closer to role expectations with targeted technical upskilling, while Brian would benefit from a more balanced plan addressing both technical foundations and adaptability.",
  };

  const pickUserSR = (data) => {
    const candidate =
      data?.userSkillGapDeltaShortReport ??
      data?.shortReports?.userSkillGapDeltaShortReport ??
      (reportKey ? data?.[reportKey] : undefined) ??
      data;

    const hasUserShape =
      candidate &&
      typeof candidate === "object" &&
      candidate.userA &&
      candidate.userB;

    const aOk = Array.isArray(candidate?.userA?.missing_skills);
    const bOk = Array.isArray(candidate?.userB?.missing_skills);

    return hasUserShape && (aOk || bOk) ? candidate : DEFAULT_SR;
  };

  const sr = pickUserSR(comparisonData);

  const personAName = sr?.userA?.name ?? "Person A";
  const personBName = sr?.userB?.name ?? "Person B";

  const aMissing = Array.isArray(sr?.userA?.missing_skills)
    ? sr.userA.missing_skills
    : [];
  const bMissing = Array.isArray(sr?.userB?.missing_skills)
    ? sr.userB.missing_skills
    : [];

  const splitByType = (arr) => {
    const out = { soft: [], job: [] };
    arr.forEach((s) => {
      if ((s?.type || "").toLowerCase() === "soft skill") {
        out.soft.push(s?.skill_gap || "-");
      } else if ((s?.type || "").toLowerCase() === "job skill") {
        out.job.push(s?.skill_gap || "-");
      }
    });
    return out;
  };

  const aSplit = splitByType(aMissing);
  const bSplit = splitByType(bMissing);

  return (
    <div className="tt-usg-short-report-container">
      <div className="tt-usg-report-header">
        <h3>Skill Gap Delta</h3>
        <p>Quick overview of gaps between the selected users</p>
      </div>

      {/* Metric Cards */}
      <div className="tt-usg-comparison-summary">
        <div className="tt-usg-summary-card">
          <div className="tt-usg-summary-icon" aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
            </svg>
          </div>
          <div className="tt-usg-summary-content">
            <div className="tt-usg-summary-label">
              {comparisonData.skillGapsShortReport.userAData.name}
            </div>
            <div className="tt-usg-summary-value">
              {
                comparisonData.skillGapsShortReport.userAData.missingJobSkills
                  .length
              }
            </div>
            <div className="tt-usg-summary-label">Missing Soft Skills</div>
          </div>
        </div>

        <div className="tt-usg-summary-card">
          <div className="tt-usg-summary-icon" aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
            </svg>
          </div>
          <div className="tt-usg-summary-content">
            <div className="tt-usg-summary-label">
              {comparisonData.skillGapsShortReport.userBData.name}
            </div>
            <div className="tt-usg-summary-value">
              {
                comparisonData.skillGapsShortReport.userBData.missingSoftSkills
                  .length
              }
            </div>
            <div className="tt-usg-summary-label">Missing Soft Skills</div>
          </div>
        </div>

        <div className="tt-usg-summary-card">
          <div className="tt-usg-summary-icon" aria-hidden="true">
            <LuBriefcase size={24} />
          </div>
          <div className="tt-usg-summary-content">
            <div className="tt-usg-summary-label">
              {comparisonData.skillGapsShortReport.userAData.name}
            </div>
            <div className="tt-usg-summary-value">
              {
                comparisonData.skillGapsShortReport.userAData.missingJobSkills
                  .length
              }
            </div>
            <div className="tt-usg-summary-label">Missing Job Skills</div>
          </div>
        </div>

        <div className="tt-usg-summary-card">
          <div className="tt-usg-summary-icon" aria-hidden="true">
            <LuBriefcase size={24} />
          </div>
          <div className="tt-usg-summary-content">
            <div className="tt-usg-summary-label">
              {comparisonData.skillGapsShortReport.userBData.name}
            </div>
            <div className="tt-usg-summary-value">
              {
                comparisonData.skillGapsShortReport.userBData.missingJobSkills
                  .length
              }
            </div>
            <div className="tt-usg-summary-label">Missing Job Skills</div>
          </div>
        </div>
      </div>

      {/* Lists instead of bars */}
      <div className="tt-usg-missing-lists">
        <div className="tt-usg-list-panel">
          <h4>{comparisonData.skillGapsShortReport.userAData.name}</h4>

          <div className="tt-usg-list-block">
            <div className="tt-usg-list-title">Missing Soft Skills</div>
            <div className="tt-usg-pill-list" role="list">
              {comparisonData.skillGapsShortReport.userBData.missingSoftSkills
                .length === 0 ? (
                <span
                  className="tt-usg-pill tt-usg-pill--empty"
                  role="listitem"
                >
                  No soft skill gaps
                </span>
              ) : (
                comparisonData.skillGapsShortReport.userBData.missingSoftSkills.map(
                  (s, i) => (
                    <span
                      className="tt-usg-pill"
                      role="listitem"
                      key={`b-soft-${i}`}
                    >
                      {s}
                    </span>
                  )
                )
              )}
            </div>
          </div>

          <div className="tt-usg-list-block">
            <div className="tt-usg-list-title">Missing Job Skills</div>
            <div className="tt-usg-pill-list" role="list">
              {comparisonData.skillGapsShortReport.userAData.missingJobSkills
                .length === 0 ? (
                <span
                  className="tt-usg-pill tt-usg-pill--empty"
                  role="listitem"
                >
                  No job skill gaps
                </span>
              ) : (
                comparisonData.skillGapsShortReport.userAData.missingJobSkills.map(
                  (s, i) => (
                    <span
                      className="tt-usg-pill"
                      role="listitem"
                      key={`a-job-${i}`}
                    >
                      {s}
                    </span>
                  )
                )
              )}
            </div>
          </div>
        </div>

        <div className="tt-usg-list-panel">
          <h4>{comparisonData.skillGapsShortReport.userBData.name}</h4>

          <div className="tt-usg-list-block">
            <div className="tt-usg-list-title">Missing Soft Skills</div>
            <div className="tt-usg-pill-list" role="list">
              {comparisonData.skillGapsShortReport.userBData.missingSoftSkills
                .length === 0 ? (
                <span
                  className="tt-usg-pill tt-usg-pill--empty"
                  role="listitem"
                >
                  No soft skill gaps
                </span>
              ) : (
                comparisonData.skillGapsShortReport.userBData.missingSoftSkills.map(
                  (s, i) => (
                    <span
                      className="tt-usg-pill"
                      role="listitem"
                      key={`b-soft-${i}`}
                    >
                      {s}
                    </span>
                  )
                )
              )}
            </div>
          </div>

          <div className="tt-usg-list-block">
            <div className="tt-usg-list-title">Missing Job Skills</div>
            <div className="tt-usg-pill-list" role="list">
              {comparisonData.skillGapsShortReport.userBData.missingJobSkills
                .length === 0 ? (
                <span
                  className="tt-usg-pill tt-usg-pill--empty"
                  role="listitem"
                >
                  No job skill gaps
                </span>
              ) : (
                comparisonData.skillGapsShortReport.userBData.missingJobSkills.map(
                  (s, i) => (
                    <span
                      className="tt-usg-pill"
                      role="listitem"
                      key={`b-job-${i}`}
                    >
                      {s}
                    </span>
                  )
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Analysis Overview */}
      <div className="tt-usg-insights-section">
        <h3>Analysis Overview</h3>
        <div className="tt-usg-analysis-paragraph">
          <p>{comparisonData.skillGapsShortReport.overview}</p>
        </div>
      </div>
    </div>
  );
};

export default UserSkillGapDeltaReport;
